import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { db, auth } from './firebase';
import { perfTracker } from './perfLogger';
import { DynamicPaymentGateway, PaymentGatewayConfig } from '../types';

const GATEWAYS_COLLECTION = 'settings';
const GATEWAYS_DOC_ID = 'payment_gateways';

export const DEFAULT_DYNAMIC_GATEWAYS: DynamicPaymentGateway[] = [
  {
    id: 'nowpayments',
    name: 'NOWPayments Crypto',
    type: 'automatic',
    description: 'Accept Bitcoin, Ethereum, USDT, SOL, TRX and 150+ cryptocurrencies with automated IPN verification.',
    badgeText: 'Crypto (150+)',
    badgeColor: '#10b981',
    logoIcon: 'Usdt',
    merchantName: 'RH Editography Crypto Desk',
    merchantAccountNumber: '',
    clientId: '',
    apiKey: '',
    secretKey: '',
    publishableKey: '',
    storeId: '',
    storePassword: '',
    webhookUrl: 'https://rheditography.net/api/webhooks/nowpayments',
    webhookSecret: '',
    callbackUrl: 'https://rheditography.net/checkout/nowpayments-callback',
    successUrl: 'https://rheditography.net/checkout/success',
    cancelUrl: 'https://rheditography.net/checkout/cancel',
    instructions: 'Select your preferred crypto currency (USDT, BTC, ETH, TRX, etc.) on NOWPayments secure checkout page.',
    buttonText: 'Pay via NOWPayments Crypto',
    enabled: true,
    testMode: false,
    displayOrder: 1,
  },
  {
    id: 'gw_1790898143227',
    name: 'BTC Pay',
    type: 'automatic',
    description: 'Direct Bitcoin Payment with on-chain verification.',
    badgeText: 'Bitcoin Payment',
    badgeColor: '#10b981',
    logoIcon: 'Bitcoin',
    merchantName: 'BTC Pay',
    merchantAccountNumber: '',
    clientId: '',
    apiKey: '',
    secretKey: '',
    publishableKey: '',
    storeId: '',
    storePassword: '',
    webhookUrl: '',
    webhookSecret: '',
    callbackUrl: '',
    successUrl: 'https://rheditography.net/checkout/success',
    cancelUrl: 'https://rheditography.net/checkout/cancel',
    instructions: 'Send exact BTC to provided merchant wallet address.',
    buttonText: 'Submit Payment',
    cryptoWallet: '1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa',
    cryptoNetwork: 'BTC',
    qrCodeUrl: '',
    enabled: true,
    testMode: true,
    displayOrder: 2,
    processingFeePercent: 0,
  },
  {
    id: 'gw_1790899255880',
    name: 'Paypal Personal',
    type: 'manual',
    description: 'Direct PayPal transfer to personal account.',
    badgeText: 'Paypal Personal Pay',
    badgeColor: '#0ea5e9',
    logoIcon: 'DollarSign',
    merchantName: 'Paypal Personal',
    merchantAccountNumber: 'payments@rheditography.net',
    clientId: '',
    apiKey: '',
    secretKey: '',
    publishableKey: '',
    storeId: '',
    storePassword: '',
    webhookUrl: '',
    webhookSecret: '',
    callbackUrl: '',
    successUrl: 'https://rheditography.net/checkout/success',
    cancelUrl: 'https://rheditography.net/checkout/cancel',
    instructions: 'Send payment via Friends & Family to paypal account: payments@rheditography.net',
    buttonText: 'Submit Payment',
    cryptoWallet: '',
    cryptoNetwork: '',
    qrCodeUrl: '',
    enabled: true,
    testMode: true,
    displayOrder: 3,
    processingFeePercent: 0,
  },
  {
    id: 'manual',
    name: 'USDT (TRC20)',
    type: 'manual',
    description: 'TRC20 Instant Transfer (KrakenPay merchant).',
    badgeText: 'Trone Crypto',
    badgeColor: '#10b981',
    logoIcon: 'Usdt',
    merchantName: 'KrakenPay',
    merchantAccountNumber: '',
    clientId: '',
    apiKey: 'rh.editography@gmail.com',
    secretKey: 'Love for rupa@1989',
    publishableKey: '',
    storeId: '',
    storePassword: '',
    webhookUrl: '',
    webhookSecret: '',
    callbackUrl: '',
    successUrl: 'https://rheditography.net/checkout/success',
    cancelUrl: 'https://rheditography.net/checkout/cancel',
    instructions: 'Pay via agreed custom arrangement or crypto wallet transfer. Admin manual approval required.',
    buttonText: 'Submit Payment',
    cryptoWallet: 'TJEZthvDVPacBYj1UMFBrqcTmx6Abupw7s',
    cryptoNetwork: 'USDT (TRC20)',
    qrCodeUrl: 'TJEZthvDVPacBYj1UMFBrqcTmx6Abupw7s',
    enabled: true,
    testMode: true,
    displayOrder: 4,
    processingFeePercent: 2,
  }
];

export function getInitialGateways(): DynamicPaymentGateway[] {
  try {
    const cached = localStorage.getItem('rh_dynamic_gateways');
    if (cached) {
      const parsed = JSON.parse(cached);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return processIncomingGateways(parsed);
      }
    }
  } catch (e) {}
  return processIncomingGateways(DEFAULT_DYNAMIC_GATEWAYS);
}

export interface PaymentGatewaysMap {
  [key: string]: DynamicPaymentGateway;
}

const DELETED_LEGACY_GATEWAY_IDS = new Set([
  'stripe',
  'paypal',
  'bybit',
  'coinbase',
  'binancepay',
  'binance',
  'helaket',
  'bitpay',
  'coingate',
  'bank_wire',
  'bankwire',
  'bank_transfer'
]);

export function filterUnwantedGateways(list: DynamicPaymentGateway[]): DynamicPaymentGateway[] {
  if (!Array.isArray(list)) return [];
  return list.filter(gw => {
    if (!gw) return false;
    const lowerId = (gw.id || '').toLowerCase().trim();
    const lowerName = (gw.name || '').toLowerCase().trim();
    if (DELETED_LEGACY_GATEWAY_IDS.has(lowerId)) return false;
    return !(
      lowerId.includes('bank_wire') ||
      lowerId.includes('bankwire') ||
      lowerId.includes('bank_transfer') ||
      lowerName.includes('bank wire') ||
      lowerName.includes('direct bank') ||
      lowerName.includes('bank transfer') ||
      lowerName.includes('stripe credit card') ||
      lowerName.includes('paypal express') ||
      lowerName.includes('bybit pay') ||
      lowerName.includes('coinbase commerce') ||
      lowerName.includes('binance pay') ||
      lowerName.includes('helaket payment') ||
      lowerName.includes('bitpay crypto') ||
      lowerName.includes('coingate crypto')
    );
  });
}

// Clean local cache on initialization to remove legacy deleted payment gateways
try {
  const cached = localStorage.getItem('rh_dynamic_gateways');
  if (cached) {
    const parsed = JSON.parse(cached);
    if (Array.isArray(parsed)) {
      const filtered = filterUnwantedGateways(parsed);
      localStorage.setItem('rh_dynamic_gateways', JSON.stringify(filtered));
    }
  }
} catch (e) {}

/**
 * Merges stored gateway configurations with the comprehensive DEFAULT_DYNAMIC_GATEWAYS catalog.
 * Honoring deletions: only gateways present in rawList are retained, preventing deleted gateways
 * from resurrecting upon refresh or refetch.
 */
function processIncomingGateways(rawList: any[]): DynamicPaymentGateway[] {
  if (!Array.isArray(rawList) || rawList.length === 0) {
    return filterUnwantedGateways(DEFAULT_DYNAMIC_GATEWAYS);
  }

  const defaultMap = new Map<string, DynamicPaymentGateway>();
  DEFAULT_DYNAMIC_GATEWAYS.forEach(defGw => defaultMap.set(defGw.id, defGw));

  // Process ONLY gateways present in rawList, preserving permanent deletions
  const processed: DynamicPaymentGateway[] = rawList
    .filter(gw => gw && gw.id)
    .map(savedGw => {
      const def = defaultMap.get(savedGw.id);
      if (def) {
        return {
          ...def,
          ...savedGw,
          enabled: typeof savedGw.enabled === 'boolean' ? savedGw.enabled : false
        };
      }
      return savedGw;
    });

  const filtered = filterUnwantedGateways(processed);
  return filtered.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
}

export function getLocalCachedPaymentGateways(): DynamicPaymentGateway[] {
  try {
    const cached = localStorage.getItem('rh_dynamic_gateways');
    if (cached) {
      const parsed = JSON.parse(cached);
      if (Array.isArray(parsed)) {
        return processIncomingGateways(parsed);
      }
    }
  } catch (e) {}
  return processIncomingGateways(DEFAULT_DYNAMIC_GATEWAYS);
}

const gatewaySubscribers = new Set<(list: DynamicPaymentGateway[]) => void>();

export async function fetchDynamicPaymentGatewaysFromFirestore(): Promise<DynamicPaymentGateway[]> {
  const startTime = performance.now();

  // 1. Try direct Firestore read
  try {
    const docRef = doc(db, GATEWAYS_COLLECTION, GATEWAYS_DOC_ID);
    const docSnap = await getDoc(docRef);
    const duration = performance.now() - startTime;

    if (docSnap.exists()) {
      const data = docSnap.data();
      if (data && Array.isArray(data.gatewaysList)) {
        const sorted = processIncomingGateways(data.gatewaysList);
        perfTracker.logFetch(`${GATEWAYS_COLLECTION}/${GATEWAYS_DOC_ID}`, duration, docSnap.metadata?.fromCache ? 'cache' : 'server', 1);
        try {
          localStorage.setItem('rh_dynamic_gateways', JSON.stringify(sorted));
        } catch (e) {}
        return sorted;
      }
    }
  } catch (e) {
    console.warn('Direct fetch payment gateways note, falling back to backend routes:', e);
  }

  // 2. Try backend admin endpoint if user has token
  try {
    const token = await auth.currentUser?.getIdToken();
    if (token) {
      const res = await fetch('/api/admin/payment-gateways', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data && data.success && Array.isArray(data.gateways) && data.gateways.length > 0) {
          const sorted = processIncomingGateways(data.gateways);
          try {
            localStorage.setItem('rh_dynamic_gateways', JSON.stringify(sorted));
          } catch (e) {}
          return sorted;
        }
      }
    }
  } catch (admErr) {}

  // 3. Fallback to public gateways endpoint
  try {
    const res = await fetch('/api/public/gateways');
    const data = await res.json();
    if (data && data.success && Array.isArray(data.gateways)) {
      const sorted = processIncomingGateways(data.gateways);
      try {
        localStorage.setItem('rh_dynamic_gateways', JSON.stringify(sorted));
      } catch (e) {}
      return sorted;
    }
  } catch (apiErr) {
    console.warn('Fallback public API failed:', apiErr);
  }

  return getLocalCachedPaymentGateways();
}

export function subscribeToDynamicPaymentGateways(onUpdate: (list: DynamicPaymentGateway[]) => void): () => void {
  onUpdate(getLocalCachedPaymentGateways());

  gatewaySubscribers.add(onUpdate);

  // Background fetch
  fetchDynamicPaymentGatewaysFromFirestore().then((sorted) => {
    if (sorted && sorted.length > 0) {
      gatewaySubscribers.forEach((cb) => {
        try { cb(sorted); } catch (e) {}
      });
    }
  }).catch((err) => {
    console.warn('Payment gateways initial fetch error:', err);
  });

  // Real-time listener when Firestore permissions allow
  let unsubscribeDoc: (() => void) | null = null;
  try {
    const docRef = doc(db, GATEWAYS_COLLECTION, GATEWAYS_DOC_ID);
    unsubscribeDoc = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data && Array.isArray(data.gatewaysList)) {
          const sorted = processIncomingGateways(data.gatewaysList);
          try {
            localStorage.setItem('rh_dynamic_gateways', JSON.stringify(sorted));
          } catch (e) {}
          gatewaySubscribers.forEach((cb) => {
            try { cb(sorted); } catch (e) {}
          });
        }
      }
    }, () => {
      // Permission denied for non-admin visitors handled gracefully
    });
  } catch (e) {}

  return () => {
    gatewaySubscribers.delete(onUpdate);
    if (unsubscribeDoc) {
      unsubscribeDoc();
    }
  };
}

export const subscribeToDynamicPaymentGatewaysFromFirestore = subscribeToDynamicPaymentGateways;

export async function saveDynamicPaymentGatewaysToFirestore(list: DynamicPaymentGateway[]): Promise<void> {
  const sanitizedList = filterUnwantedGateways(list);

  try {
    localStorage.setItem('rh_dynamic_gateways', JSON.stringify(sanitizedList));
  } catch (e) {
    console.warn('Cache save error:', e);
  }

  // 1. Save to Firestore
  let firestoreSaved = false;
  try {
    const docRef = doc(db, GATEWAYS_COLLECTION, GATEWAYS_DOC_ID);
    await setDoc(docRef, {
      gatewaysList: sanitizedList,
      updatedAt: new Date().toISOString()
    });
    firestoreSaved = true;
  } catch (err) {
    console.warn('Direct Firestore save note, will sync to backend admin route:', err);
  }

  // 2. Sync to Backend Admin API
  try {
    const token = await auth.currentUser?.getIdToken();
    await fetch('/api/admin/payment-gateways', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      body: JSON.stringify({ gateways: sanitizedList })
    });
  } catch (apiErr) {
    console.warn('Backend payment gateways sync error:', apiErr);
    if (!firestoreSaved) {
      throw apiErr;
    }
  }
}

// Backwards compatibility wrappers
export function subscribeToPaymentGateways(onUpdate: (map: any) => void) {
  return subscribeToDynamicPaymentGateways((list) => {
    onUpdate(list);
  });
}

export async function savePaymentGatewaysToFirestore(data: any): Promise<void> {
  if (Array.isArray(data)) {
    return saveDynamicPaymentGatewaysToFirestore(data);
  }
  if (data && Array.isArray(data.gatewaysList)) {
    return saveDynamicPaymentGatewaysToFirestore(data.gatewaysList);
  }
}
